/**
 * Shared IOSFlow storage helpers.
 *
 * Every file that needs to read / write / clear IOSFlow flags should import
 * from here so that sessionStorage ↔ localStorage stays consistent.
 *
 * ARCHITECTURE NOTE (v2):
 * - Flow flags are stored in BOTH sessionStorage and localStorage for
 *   cross-restart persistence on native.
 * - `IOSFLOW_ONBOARDING_COMPLETED_KEY` is now treated as a cosmetic marker
 *   ("questionnaire finished") and NEVER used for routing decisions.
 * - Access decisions follow: Auth → DB subscription status → sync recovery.
 */

export const IOSFLOW_PENDING_FINALIZE_KEY = 'iosflow_pending_finalize';
export const IOSFLOW_RESTORE_ATTEMPTED_KEY = 'iosflow_restore_attempted';
export const IOSFLOW_ONBOARDING_DATA_KEY = 'iosflow_onboarding_data';
export const IOSFLOW_RC_TEMP_ID_KEY = 'iosflow_rc_temp_id';
export const IOSFLOW_ONBOARDING_COMPLETED_KEY = 'iosflow_onboarding_completed';
export const IOSFLOW_V2_KEY = 'iosflow_v2';
export const IOSFLOW_V2_PERSISTENT_KEY = 'iosflow_v2_persistent';
export const IOSFLOW_RETURNING_USER_KEY = 'iosflow_returning_user';
/** V2-only: marks that the user purchased/restored and needs finalize after Apple sign-in. Values: 'purchase' | 'restore'. */
export const IOSFLOW_V2_FINALIZE_REASON_KEY = 'iosflow_v2_finalize_reason';
export const ONBOARDING_COMPLETED_KEY = 'onboarding_completed';
export const ONBOARDING_COMPLETED_USER_ID_KEY = 'onboarding_completed_user_id';

// ─── V2-only session-scoped helpers ───
// V2 flags are stored in sessionStorage ONLY to prevent cross-user poisoning.

/** Read a V2 session flag (sessionStorage only). */
export const readV2Flag = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(key) === 'true';
};

/** Write a V2 session flag (sessionStorage only). */
export const setV2Flag = (key: string, value = 'true') => {
  sessionStorage.setItem(key, value);
};

/** Clear a V2 session flag (sessionStorage only). */
export const clearV2Flag = (key: string) => {
  sessionStorage.removeItem(key);
};

/** Read V2 session data (sessionStorage only). */
export const readV2Data = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(key);
};

/** Write V2 session data (sessionStorage only). */
export const setV2Data = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
};

/** Clear V2 session data (sessionStorage only). */
export const clearV2Data = (key: string) => {
  sessionStorage.removeItem(key);
};

/**
 * Wipe every V2-specific session flag.
 * Safe to call on sign-out or flow reset.
 */
export const clearAllV2State = () => {
  clearV2Flag(IOSFLOW_PENDING_FINALIZE_KEY);
  clearV2Flag(IOSFLOW_RESTORE_ATTEMPTED_KEY);
  clearV2Data(IOSFLOW_ONBOARDING_DATA_KEY);
  clearV2Data(IOSFLOW_RC_TEMP_ID_KEY);
  clearV2Flag(IOSFLOW_V2_KEY);
  clearV2Flag(IOSFLOW_RETURNING_USER_KEY);
  clearV2Data(IOSFLOW_V2_FINALIZE_REASON_KEY);
  // Clear persistent V2 flag (localStorage) — flow completed successfully
  localStorage.removeItem(IOSFLOW_V2_PERSISTENT_KEY);
};

// ─── Hybrid read / write / clear ───

/** Read a boolean flag from sessionStorage OR localStorage. */
export const readFlowFlag = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(key) === 'true' || localStorage.getItem(key) === 'true';
};

/** Write a boolean flag to BOTH sessionStorage and localStorage. */
export const setFlowFlag = (key: string, value = 'true') => {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
};

/** Remove a flag from BOTH stores. */
export const clearFlowFlag = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

/** Read arbitrary string data (session first, then local). */
export const readFlowData = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
};

/** Write arbitrary string data to both stores. */
export const setFlowData = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
};

/** Remove arbitrary string data from both stores. */
export const clearFlowData = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

/**
 * Wipe every IOSFlow flag/data key from both stores.
 * Call this on signed-out cold-starts to guarantee a fresh beginning.
 * Also clears the onboarding-completed marker and RC temp ID.
 */
export const clearAllIosflowState = () => {
  clearFlowFlag(IOSFLOW_PENDING_FINALIZE_KEY);
  clearFlowFlag(IOSFLOW_RESTORE_ATTEMPTED_KEY);
  clearFlowData(IOSFLOW_ONBOARDING_DATA_KEY);
  clearFlowData(IOSFLOW_RC_TEMP_ID_KEY);
  localStorage.removeItem(IOSFLOW_ONBOARDING_COMPLETED_KEY);
  clearFlowFlag(IOSFLOW_V2_KEY);
  // Also clear onboarding completed keys to prevent stale routing
  localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
  localStorage.removeItem(ONBOARDING_COMPLETED_USER_ID_KEY);
};

/**
 * Regenerate the anonymous RevenueCat temp ID.
 * Call this when a flow session is abandoned or a different auth journey starts,
 * to prevent identity/receipt confusion on shared devices.
 */
export const resetRevenueCatTempId = () => {
  clearFlowData(IOSFLOW_RC_TEMP_ID_KEY);
};
